import { ApplicationConfig, provideBrowserGlobalErrorListeners, isDevMode, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CORE_INTERCEPTORS, provideCore } from '@corelabs/angular/core';
import { provideAccount } from '@corelabs/angular/account';
import { provideMessages } from '@corelabs/angular/messages';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';
import { environment } from '@env/environment';

import { acceptLanguageInterceptor, addAssetsPrefixInterceptor, HttpService } from './core/http';
import { appRoutes } from './app.routing';
import { provideMissingTranslationHandler, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { CustomMissingTranslationHandler, TranslateLoaderStatic } from './i18n';
import { imagesStoreProviders } from './store/images';
import { userStoreProviders } from './store/user';
import { nextStoreProviders } from './store/next';
import { courseStoreProviders } from './store/course';
import { courseAssessmentStoreProviders } from './store/course-assessment';
import { secureUploadStoreProviders } from './store/secure-upload';
import { lessonStoreProviders } from './store/lesson';
import { moduleStoreProviders } from './store/module';
import { quizStoreProviders } from './store/quiz';
import { quizProgressStoreProviders } from './store/quiz-progress';
import { reflectionStoreProviders } from './store/reflection';
import { schoolStoreProviders } from './store/school';
import { progressStoreProviders } from './store/progress';
import { pathwayStoreProviders } from './store/pathways';
import { organisationStoreProviders } from './store/organisation';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withComponentInputBinding(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
        ),
        provideAnimations(),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideHttpClient(withInterceptors([...CORE_INTERCEPTORS, acceptLanguageInterceptor, addAssetsPrefixInterceptor])),
        provideStore(),
        provideStoreDevtools({
            maxAge: 25,
            logOnly: environment.production,
            connectInZone: true,
        }),
        provideEffects(),
        provideCore({ serverUrl: environment.serverUrl + 'api/v1/' }),
        // After provideCore() so the app's HttpService (corelabs client + app fluent methods) wins the HttpClient token.
        {
            provide: HttpClient,
            useClass: HttpService,
        },
        ...imagesStoreProviders,
        provideAccount({ skip: ['logout-error'] }),
        provideMessages({ secondsShown: 5, visibleCount: 2, updateInterval: 100 }),
        ...userStoreProviders,
        ...nextStoreProviders,
        ...courseStoreProviders,
        ...courseAssessmentStoreProviders,
        ...secureUploadStoreProviders,
        ...lessonStoreProviders,
        ...moduleStoreProviders,
        ...quizStoreProviders,
        ...quizProgressStoreProviders,
        ...reflectionStoreProviders,
        ...schoolStoreProviders,
        ...progressStoreProviders,
        ...pathwayStoreProviders,
        ...organisationStoreProviders,
        provideTranslateService({
            loader: provideTranslateLoader(TranslateLoaderStatic),
            missingTranslationHandler: provideMissingTranslationHandler(CustomMissingTranslationHandler),
        }),
    ],
};
